import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LogIn,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import { authApi, DEVICE_STORAGE_KEY } from '@/features/auth/api'
import { GoogleGlyph, MicrosoftGlyph } from '@/features/auth/BrandGlyphs'
import type { CognitoSigninResponse } from '@/features/auth/types'

const errMsg = (
  err: unknown,
  fallback = "We couldn't sign you in. Please try again in a moment.",
) => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

const clearLocalDeviceMeta = () => {
  try {
    localStorage.removeItem(DEVICE_STORAGE_KEY)
  } catch {
    /* localStorage unavailable - nothing to clear */
  }
}

interface DeviceMeta {
  email: string
  deviceKey: string
  deviceGroupKey: string
  devicePassword: string
  expiresAt: number
}

const loadDeviceMeta = (): DeviceMeta | null => {
  try {
    const raw = localStorage.getItem(DEVICE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as DeviceMeta
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
      localStorage.removeItem(DEVICE_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function LoginPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [params] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [step, setStep] = useState<'password' | 'totp'>('password')
  const [totp, setTotp] = useState('')
  const [pending, setPending] = useState<CognitoSigninResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showResendLink, setShowResendLink] = useState(false)
  const [busy, setBusy] = useState(false)

  // Accept-invite landing routes already-registered invitees here with
  // ?exists=1 so we can tell them why they're on /login instead of /signup.
  const inviteExistsHint = params.get('exists') === '1' && !!params.get('invite')

  useEffect(() => {
    const e = params.get('error')
    if (!e) return
    const map: Record<string, string> = {
      google_not_configured: 'Google login is not available right now.',
      google_denied: 'Google login was cancelled.',
      google_no_email: 'Could not retrieve your email from Google.',
      microsoft_not_configured: 'Microsoft login is not available right now.',
      microsoft_denied: 'Microsoft login was cancelled.',
      staff_email_social: 'This email is registered as Bokapsys staff. Sign in at /admin-login.',
      google_state: 'Sign-in session expired. Please try again.',
      microsoft_state: 'Sign-in session expired. Please try again.',
    }
    setError(map[e] ?? 'Login failed. Please try again.')
  }, [params])

  const next = params.get('next') || '/dashboard'

  const completeSignin = async (data: CognitoSigninResponse) => {
    if (!data.idToken) throw new Error('Missing Cognito ID token.')
    const session = await authApi.cognitoSession(data.idToken)

    // Multi-tenant fork: user belongs to 2+ customer tenants. Stash the
    // 5-minute pending_session token in sessionStorage (tab-scoped) and
    // route to /workspaces where they pick one. No App JWT is issued
    // until they pick - the picker page swaps pending → real via
    // /api/auth/select-tenant.
    if (session.needsTenantSelection && session.pendingSessionToken) {
      // Defensively clear any stale App JWT so /workspaces boots in
      // pending mode rather than trying (and failing) to fetch
      // memberships with a dead token. See comment in
      // socialAuth.js:sendOAuthPickerHandoff for the same fix on the
      // OAuth callback side.
      tokenManager.clear()
      localStorage.removeItem('userId')
      localStorage.removeItem('userName')
      localStorage.removeItem('userEmail')
      sessionStorage.setItem('bk.pending_session', session.pendingSessionToken)
      sessionStorage.setItem(
        'bk.pending_memberships',
        JSON.stringify(session.memberships ?? [])
      )
      if (session.email) sessionStorage.setItem('bk.pending_email', session.email)
      if (session.name) sessionStorage.setItem('bk.pending_name', session.name)
      navigate('/workspaces', {
        replace: true,
        state: { memberships: session.memberships, next },
      })
      return
    }

    if (!session.token) throw new Error(session.error || 'Session exchange failed.')
    tokenManager.set(session.token)
    if (session.userId) localStorage.setItem('userId', String(session.userId))
    if (session.name) localStorage.setItem('userName', session.name)
    if (session.email) localStorage.setItem('userEmail', session.email)

    navigate(next, { replace: true })
  }

  const handleResult = async (data: CognitoSigninResponse) => {
    // Backend cleared a stale "remembered device" record on Cognito as
    // part of recovering this sign-in. Drop our local cache too so the
    // next attempt starts fresh - otherwise we'd keep sending the now-
    // forgotten device key and could land in the same loop.
    if (data.deviceForgotten) clearLocalDeviceMeta()

    if (data.step === 'done') {
      await completeSignin(data)
      return
    }
    if (data.step === 'totp') {
      setPending(data)
      setStep('totp')
      return
    }
    if (data.step === 'totp_setup') {
      setError(
        "Two-factor authentication needs to be set up before you can sign in. Please open the Security page once you're signed in, or contact support if you need help.",
      )
      return
    }
    if (data.step === 'new_password') {
      setError(
        'For security, your password needs to be reset before you can sign in. Please use the "Forgot?" link to set a new one.',
      )
      return
    }
    // Defensive fallback - should not be reachable now that the backend
    // handles every Cognito challenge explicitly. We still want to be
    // honest with the user about why they can't continue and what to do.
    setError(
      "Your sign-in needs an extra step we don't support yet. Please refresh the page and try again, or contact support if it keeps happening.",
    )
  }

  const submitPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setShowResendLink(false)
    if (!email || !password) {
      setError('Please enter both your email and password to continue.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address - for example, name@example.com.')
      return
    }
    setBusy(true)
    try {
      const meta = loadDeviceMeta()
      const deviceKey = meta && meta.email === email ? meta.deviceKey : null
      try {
        const data = await authApi.cognitoSignin({ email, password, deviceKey })
        await handleResult(data)
        return
      } catch (err) {
        // Backend told us it cleaned up a stale "remembered device" record
        // and that we should sign in fresh. Drop our local device cache
        // and transparently retry once with no device key - that should
        // succeed and put the user straight into the TOTP step.
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          err.body &&
          typeof err.body === 'object' &&
          'code' in err.body &&
          (err.body as { code: unknown }).code === 'DEVICE_TRUST_RESET'
        ) {
          clearLocalDeviceMeta()
          try {
            const retry = await authApi.cognitoSignin({ email, password, deviceKey: null })
            await handleResult(retry)
            return
          } catch (retryErr) {
            setError(errMsg(retryErr))
            return
          }
        }
        if (err instanceof ApiError && err.status === 503) {
          const data = await authApi.legacyLogin(email, password)
          // Multi-tenant fork (same shape as the Cognito session exchange).
          if (data.needsTenantSelection && data.pendingSessionToken) {
            tokenManager.clear()
            localStorage.removeItem('userId')
            localStorage.removeItem('userName')
            localStorage.removeItem('userEmail')
            sessionStorage.setItem('bk.pending_session', data.pendingSessionToken)
            sessionStorage.setItem(
              'bk.pending_memberships',
              JSON.stringify(data.memberships ?? []),
            )
            if (data.email) sessionStorage.setItem('bk.pending_email', data.email)
            if (data.name) sessionStorage.setItem('bk.pending_name', data.name)
            navigate('/workspaces', {
              replace: true,
              state: { memberships: data.memberships, next },
            })
            return
          }
          if (!data.token) throw new Error(data.error || 'Login failed.')
          tokenManager.set(data.token)
          if (data.userId) localStorage.setItem('userId', String(data.userId))
          if (data.name) localStorage.setItem('userName', data.name)
          if (data.email) localStorage.setItem('userEmail', data.email)
          navigate(next, { replace: true })
          return
        }
        if (err instanceof ApiError && err.status === 403 && err.body && typeof err.body === 'object' && 'isVerifiedError' in err.body) {
          setError(errMsg(err))
          setShowResendLink(true)
          return
        }
        throw err
      }
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  const submitTotp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    if (!/^\d{6}$/.test(totp)) {
      setError('Please enter the 6-digit code from your authenticator app.')
      return
    }
    if (!pending?.session) {
      setError('Your sign-in attempt timed out. Please re-enter your password to start again.')
      setStep('password')
      return
    }
    setBusy(true)
    try {
      const data = await authApi.cognitoConfirm({
        email,
        session: pending.session,
        challengeName: 'SOFTWARE_TOKEN_MFA',
        responses: { SOFTWARE_TOKEN_MFA_CODE: totp },
      })
      // Backend forgot a stuck device record - drop our cache so the
      // next attempt starts clean. Same rationale as in handleResult.
      if (data.deviceForgotten) clearLocalDeviceMeta()
      await handleResult(data)
    } catch (err) {
      // Backend signalled MFA_RESTART (wrong code, expired session, or
      // post-MFA device cleanup). Bounce the user back to the password
      // step with the message the backend gave us - the password page
      // can issue a fresh Cognito challenge.
      if (
        err instanceof ApiError &&
        err.body &&
        typeof err.body === 'object' &&
        'code' in err.body &&
        ((err.body as { code: unknown }).code === 'MFA_RESTART' ||
          (err.body as { code: unknown }).code === 'DEVICE_TRUST_RESET')
      ) {
        if ((err.body as { code: unknown }).code === 'DEVICE_TRUST_RESET') {
          clearLocalDeviceMeta()
        }
        setError(errMsg(err))
        setPending(null)
        setTotp('')
        setStep('password')
        return
      }
      setError(
        errMsg(
          err,
          "We couldn't verify that code. Please try the new 6-digit code from your authenticator app.",
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  const resendVerification = async () => {
    try {
      const r = await authApi.resendVerification(email)
      toast({ title: r.message ?? 'Verification email sent.' })
      setShowResendLink(false)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  return (
    <div className="grid overflow-hidden rounded-[28px] bg-card shadow-2xl ring-1 ring-border/60 lg:grid-cols-2">
      {/* Left: login form */}
      <div className="flex flex-col justify-center bg-card px-6 py-10 sm:px-10 sm:py-12 lg:px-14">
        <div className="mb-10 flex justify-center lg:justify-start">
          <img
            src="/images/logo.png"
            alt="Bokapsys"
            className="h-14 w-auto sm:h-16"
          />
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          Welcome back
        </p>
        <h1 className="mt-2 text-[28px] font-semibold leading-tight tracking-tight text-foreground sm:text-[32px]">
          Sign in to your<br className="hidden sm:inline" /> account
        </h1>

        <div className="mt-8">
          {inviteExistsHint ? (
            <Alert className="mb-4">
              <AlertDescription>
                You&apos;re already registered with Bokapsys. Sign in below to finish joining the
                workspace you were invited to.
              </AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {error}
                {showResendLink ? (
                  <>
                    {' '}
                    <Button
                      type="button"
                      variant="link"
                      onClick={resendVerification}
                      className="h-auto p-0 font-semibold underline"
                    >
                      Resend verification email
                    </Button>
                  </>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}

          {step === 'password' ? (
            <form onSubmit={submitPassword} className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="login-email"
                  className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Email
                </label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-end justify-between">
                  <label
                    htmlFor="login-password"
                    className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Forgot?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPwd ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="h-11 pr-10"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-1 top-1/2 size-9 -translate-y-1/2"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="h-11 w-full text-sm font-semibold"
                disabled={busy}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <LogIn className="size-4" />
                )}
                Sign In
              </Button>

              <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                Or continue with
                <span className="h-px flex-1 bg-border" />
              </div>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => (window.location.href = '/api/auth/google')}
                  className="h-11 w-full gap-2 font-medium"
                >
                  <GoogleGlyph />
                  Continue with Google
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => (window.location.href = '/api/auth/microsoft')}
                  className="h-11 w-full gap-2 font-medium"
                >
                  <MicrosoftGlyph />
                  Continue with Microsoft
                </Button>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                New here?{' '}
                <Link to="/signup" className="font-semibold text-primary hover:underline">
                  Create an account
                </Link>
              </p>
            </form>
          ) : (
            <form onSubmit={submitTotp} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app for{' '}
                <span className="font-medium text-foreground">{email}</span>.
              </p>
              <div className="space-y-1.5">
                <label
                  htmlFor="login-totp"
                  className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Authenticator code
                </label>
                <Input
                  id="login-totp"
                  value={totp}
                  onChange={(e) => setTotp(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  inputMode="numeric"
                  className="h-12 text-center font-mono text-xl tracking-[10px]"
                  placeholder="123456"
                  autoFocus
                />
              </div>
              <Button type="submit" className="h-11 w-full" disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                Verify and sign in
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="w-full"
                onClick={() => {
                  setStep('password')
                  setPending(null)
                  setTotp('')
                }}
              >
                ← Use a different account
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Right: trust panel - hidden on mobile, visible lg+ */}
      <TrustPanel />
    </div>
  )
}

function TrustPanel() {
  return (
    <div className="relative hidden overflow-hidden bg-primary-gradient p-10 text-primary-foreground lg:flex lg:flex-col lg:gap-7">
      {/* Soft decorative circles - echo the reference design without being noisy */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-20 -top-24 size-[320px] rounded-full bg-white/5"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-16 size-[260px] rounded-full bg-white/5"
      />

      <span className="relative inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 backdrop-blur">
        <ShieldCheck className="size-4" aria-hidden />
        100% Risk-Free
      </span>

      <div className="relative">
        <h2 className="text-[34px] font-bold leading-[1.1] tracking-tight">
          Start free.<br />
          No credit card.<br />
          No commitment.
        </h2>
        <p className="mt-3 text-sm text-white/80">
          Explore every feature at your own pace. Upgrade when you&apos;re ready.
        </p>
      </div>

      <ul className="relative space-y-4 text-sm">
        <TrustFeature
          icon={<CreditCard className="size-5" aria-hidden />}
          title="No payment details required"
          body="Sign up with just your email. We will never ask for bank or card information."
        />
        <TrustFeature
          icon={<Sparkles className="size-5" aria-hidden />}
          title="Full access on free tier"
          body="Upload statements, run reports, use AI classification and more."
        />
        <TrustFeature
          icon={<Clock className="size-5" aria-hidden />}
          title="Upgrade on your timeline"
          body="No pressure. Choose a plan only when you're ready."
        />
        <TrustFeature
          icon={<CheckCircle2 className="size-5" aria-hidden />}
          title="Cancel anytime"
          body="No lock-ins, no penalties, no hassle."
        />
      </ul>

      <div className="relative mt-auto flex items-center gap-3 rounded-xl bg-white/10 p-4 text-sm text-white/90 ring-1 ring-white/20 backdrop-blur">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
          <Lock className="size-4" aria-hidden />
        </span>
        <span>Your data is encrypted and secure. We never share your information.</span>
      </div>
    </div>
  )
}

function TrustFeature({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode
  title: string
  body: string
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/15 text-white ring-1 ring-white/20">
        {icon}
      </span>
      <div>
        <p className="text-[15px] font-semibold">{title}</p>
        <p className="mt-0.5 text-[13px] text-white/75">{body}</p>
      </div>
    </li>
  )
}

